import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import webConfig from "@/config/webConfig";
import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import {
  Calendar, Phone, MessageCircle, ArrowLeft, ChevronRight, MapPin,
  Clock, Shield, Stethoscope, CheckCircle2, AlertCircle, User, Mail,
  FileText, Building2, Sparkles, Heart
} from "lucide-react";

const API = webConfig.API_URL;

const whoShouldBook = [
  "Abnormal vaginal bleeding or discharge",
  "Ovarian cysts, fibroids, or pelvic mass",
  "Persistent pelvic or abdominal pain",
  "Suspected or confirmed gynecologic cancer",
  "Need for second opinion on surgery or reports",
  "Endometriosis symptoms affecting daily life",
  "Requirement for cancer screening (Pap smear / HPV test)",
];

const consultationTypes = [
  "Cancer Evaluation & Treatment Planning",
  "Second Opinion Consultation",
  "Benign Gynecology (Fibroids, Cysts, Endometriosis)",
  "Preventive Screening (Pap Smear, HPV Testing)",
  "Surgical Consultation (Laparoscopy / Robotic / HIPEC)",
];
const hospitals = webConfig.HOSPITALS;
const afterBooking = [
  { text: "Our team will review your request", icon: FileText },
  { text: "You will receive appointment confirmation", icon: CheckCircle2 },
  { text: "Required documents or reports will be advised", icon: FileText },
  { text: "Consultation details shared via call or WhatsApp", icon: Phone },
];

const trustPoints = [
  "Specialist in Gynecologic Oncology",
  "Expertise in advanced cancer surgery (HIPEC, robotic, laparoscopic)",
  "Clear and compassionate communication",
  "Evidence-based treatment planning",
  "Associated with leading Kolkata hospitals",
  "Focus on early diagnosis and better outcomes",
];
const formFields = [
  {
    label: "Full Name",
    name: "name",
    type: "text",
    placeholder: "Enter your full name",
    required: true,
    icon: User,
  },

  {
    label: "Phone Number",
    name: "phone",
    type: "tel",
    placeholder: "+91 98765 43210",
    required: true,
    icon: Phone,
  },

  {
    label: "Email Address",
    name: "email",
    type: "email",
    placeholder: "your@email.com",
    required: false,
    icon: Mail,
  },

  {
    label: "Preferred Hospital",
    name: "hospital",
    type: "select",
    required: false,
    icon: Building2,
    options: hospitals,
  },

  {
    label: "Consultation Type",
    name: "consultation_type",
    type: "select",
    required: false,
    icon: Stethoscope,
    options: consultationTypes,
  },

  {
    label: "Brief Description / Message",
    name: "message",
    type: "textarea",
    placeholder:
      "Briefly describe your condition or reason for consultation...",
    required: false,
  },
];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

export default function BookAppointmentPage() {

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    hospital: "",
    consultation_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    // Name validation
    if (name === "name") {
      updatedValue = value.replace(/[^A-Za-z\s]/g, "");
    }

    // Phone validation
    if (name === "phone") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // Message maxlength
    if (name === "message") {
      updatedValue = value.slice(0, 50);
    }

    setForm((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    let newErrors = {};

    // Name
    if (!form.name.trim()) {
      newErrors.name = "Full name is required";
    }

    // Phone
    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (form.phone.length !== 10) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    // Email
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)
    ) {
      newErrors.email = "Invalid email address";
    }

    // Hospital
    if (!form.hospital) {
      newErrors.hospital = "Please select hospital";
    }

    // Consultation
    if (!form.consultation_type) {
      newErrors.consultation_type = "Please select consultation type";
    }
    if (!form.message.trim()) {
      newErrors.message = "Message is required";
    }
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e) => {

    e.preventDefault();

    setError("");

    if (!validateForm()) return;

    setSubmitting(true);

    try {

      const payload = {

        FullName: form.name,
        PreferredHospital: form.hospital,
        Email: form.email,
        Phone: form.phone,
        ConsultationType: form.consultation_type,
        Message: form.message,

      };

      const response = await fetch(
        `${API}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit appointment request");
      }

      await response.json();

      setSubmitted(true);

      setForm({
        name: "",
        phone: "",
        email: "",
        hospital: "",
        consultation_type: "",
        message: "",
      });

      setErrors({});

    }
    catch (err) {

      console.log(err);

      setError("Failed to submit appointment request");

    }
    finally {

      setSubmitting(false);

    }

  };
  return (
    <>
      <Helmet>
        <title>
          Book Appointment with Dr Neha Agarwal | Gynecologic Oncologist Kolkata
        </title>

        <meta
          name="description"
          content="Book an appointment with Dr Neha Agarwal, gynecologic oncologist in Kolkata for ovarian, cervical, uterine cancer treatment, robotic surgery and women's health consultation."
        />

        <meta
          name="keywords"
          content="book gynecologic oncologist Kolkata, Dr Neha Agarwal appointment, ovarian cancer consultation Kolkata, robotic gynecologic surgery Kolkata"
        />
      </Helmet>
      <div className="bg-[#F9F9F6] min-h-screen">
        {/* ─── HEADER ─── */}
        {/* <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F9F9F6]/80 border-b border-[#1E3F33]/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2" data-testid="booking-header-logo">
              <span className="font-serif text-2xl md:text-3xl font-semibold text-[#1E3F33] tracking-tight">
                Dr. Neha Agarwal
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="booking-back-home"
                className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#4A6359] hover:text-[#1E3F33] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
              <a
                href={`https://wa.me/${webConfig.WHATSAPP}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-header-whatsapp"
                className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </div>
        </header> */}

        {/* ─── HERO ─── */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-20" data-testid="booking-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {/* Breadcrumb */}
            <motion.nav
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-2 text-sm text-[#4A6359] mb-8"
              data-testid="booking-breadcrumb"
            >
              <Link to="/" className="hover:text-[#1E3F33] transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-[#1E3F33] font-medium">Contact </span>
            </motion.nav>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Schedule a Consultation
              </p>
              <h1
                data-testid="booking-page-title"
                className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-[#1E3F33] mb-6"
              >
                Contact Dr. Neha Agarwal – Gynecologic Oncologist in Kolkata
              </h1>
              <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed" data-testid="booking-intro">
                If you would like to book a consultation, seek a second opinion, or inquire about treatment options, you can easily reach Dr. Neha Agarwal’s team.We are here to help patients with gynecologic cancer care, HIPEC, robotic surgery, laparoscopic surgery, cancer screening, and women’s health conditions.For urgent concerns, WhatsApp or phone call is the fastest way to connect.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ─── MAIN CONTENT: FORM + SIDEBAR ─── */}
        <section className="pb-20 md:pb-28" data-testid="booking-form-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              {/* LEFT: FORM */}
              <div className="lg:col-span-7">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                  className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-10"
                >


                  {submitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      data-testid="booking-success-message"
                      className="text-center py-12"
                    >
                      <div className="w-16 h-16 rounded-full bg-[#1E3F33]/10 flex items-center justify-center mx-auto mb-5">
                        <CheckCircle2 className="w-8 h-8 text-[#1E3F33]" />
                      </div>
                      <h3 className="font-serif text-2xl text-[#1E3F33] mb-3">
                        Message Sent Successfully
                      </h3>
                      <p className="text-[#4A6359] mb-6 max-w-sm mx-auto">
                        Thank you for contacting us. Our team will get back to you shortly.
                      </p>
                      <button
                        onClick={() => { setSubmitted(false); setForm({ name: "", phone: "", email: "", hospital: "", consultation_type: "", message: "" }); }}
                        data-testid="booking-submit-another"
                        className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-7 py-3 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
                      >
                        Send Another Message
                      </button>
                    </motion.div>
                  ) : (
                    <>

                      <div className="flex items-center gap-3 mb-2">
                        <Calendar className="w-5 h-5 text-[#1E3F33]" />

                        <h2 className="font-serif text-2xl sm:text-3xl text-[#1E3F33]" data-testid="booking-form-heading">
                          Contact Us
                        </h2>
                      </div>

                      <p className="text-sm text-[#4A6359] mb-8">
                        Fill in the form below. Our team will confirm your appointment timing and hospital location shortly.
                      </p>

                      <form onSubmit={handleSubmit} data-testid="booking-form" className="space-y-5">

                        {/* Name */}
                        <div>
                          <label className="block text-sm font-semibold text-[#1E3F33] mb-1.5">
                            Full Name <span className="text-[#C29F60]">*</span>
                          </label>

                          <div className="relative">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8FA998]" />

                            <input
                              type="text"
                              name="name"
                              value={form.name}
                              maxLength={30}
                              onChange={handleChange}
                              placeholder="Enter your full name"
                              data-testid="booking-input-name"
                              className={`w-full pl-11 pr-4 py-3.5 bg-[#F9F9F6] rounded-xl text-sm text-[#1E3F33] placeholder:text-[#8FA998] focus:outline-none focus:ring-2 transition-all border ${errors.name
                                ? "border-red-500 focus:ring-red-200"
                                : "border-[#D8DDD9] focus:ring-[#1E3F33]/20 focus:border-[#1E3F33]"
                                }`}
                            />
                          </div>

                          {errors.name && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.name}
                            </p>
                          )}
                        </div>

                        {/* Phone */}
                        <div>
                          <label className="block text-sm font-semibold text-[#1E3F33] mb-1.5">
                            Phone Number <span className="text-[#C29F60]">*</span>
                          </label>

                          <div className="relative">
                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8FA998]" />

                            <input
                              type="tel"
                              name="phone"
                              value={form.phone}
                              onChange={handleChange}
                              placeholder="9876543210"
                              maxLength={10}
                              data-testid="booking-input-phone"
                              className={`w-full pl-11 pr-4 py-3.5 bg-[#F9F9F6] rounded-xl text-sm text-[#1E3F33] placeholder:text-[#8FA998] focus:outline-none focus:ring-2 transition-all border ${errors.phone
                                ? "border-red-500 focus:ring-red-200"
                                : "border-[#D8DDD9] focus:ring-[#1E3F33]/20 focus:border-[#1E3F33]"
                                }`}
                            />
                          </div>

                          {errors.phone && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.phone}
                            </p>
                          )}
                        </div>

                        {/* Email */}
                        <div>
                          <label className="block text-sm font-semibold text-[#1E3F33] mb-1.5">
                            Email Address <span className="text-[#C29F60]">*</span>
                          </label>

                          <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8FA998]" />

                            <input
                              type="email"
                              name="email"
                              value={form.email}
                              maxLength={30}
                              onChange={handleChange}
                              placeholder="your@email.com"
                              data-testid="booking-input-email"
                              className={`w-full pl-11 pr-4 py-3.5 bg-[#F9F9F6] rounded-xl text-sm text-[#1E3F33] placeholder:text-[#8FA998] focus:outline-none focus:ring-2 transition-all border ${errors.email
                                ? "border-red-500 focus:ring-red-200"
                                : "border-[#D8DDD9] focus:ring-[#1E3F33]/20 focus:border-[#1E3F33]"
                                }`}
                            />
                          </div>

                          {errors.email && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.email}
                            </p>
                          )}
                        </div>

                        {/* Hospital */}
                        <div>
                          <label className="block text-sm font-semibold text-[#1E3F33] mb-1.5">
                            Preferred Hospital <span className="text-[#C29F60]">*</span>
                          </label>

                          <div className="relative">
                            <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8FA998]" />

                            <select
                              name="hospital"
                              value={form.hospital}
                              onChange={handleChange}
                              data-testid="booking-select-hospital"
                              className={`w-full pl-11 pr-4 py-3.5 bg-[#F9F9F6] rounded-xl text-sm text-[#1E3F33] focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer border ${errors.hospital
                                ? "border-red-500 focus:ring-red-200"
                                : "border-[#D8DDD9] focus:ring-[#1E3F33]/20 focus:border-[#1E3F33]"
                                }`}
                            >
                              <option value="">Select a hospital</option>

                              {hospitals.map((h) => (
                                <option key={h} value={h}>
                                  {h}
                                </option>
                              ))}
                            </select>
                          </div>

                          {errors.hospital && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.hospital}
                            </p>
                          )}
                        </div>

                        {/* Consultation Type */}
                        <div>
                          <label className="block text-sm font-semibold text-[#1E3F33] mb-1.5">
                            Consultation Type <span className="text-[#C29F60]">*</span>
                          </label>

                          <div className="relative">
                            <Stethoscope className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8FA998]" />

                            <select
                              name="consultation_type"
                              value={form.consultation_type}
                              onChange={handleChange}
                              data-testid="booking-select-type"
                              className={`w-full pl-11 pr-4 py-3.5 bg-[#F9F9F6] rounded-xl text-sm text-[#1E3F33] focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer border ${errors.consultation_type
                                ? "border-red-500 focus:ring-red-200"
                                : "border-[#D8DDD9] focus:ring-[#1E3F33]/20 focus:border-[#1E3F33]"
                                }`}
                            >
                              <option value="">Select consultation type</option>

                              {consultationTypes.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>

                          {errors.consultation_type && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.consultation_type}
                            </p>
                          )}
                        </div>

                        {/* Message */}
                        <div>
                          <label className="block text-sm font-semibold text-[#1E3F33] mb-1.5">
                            Brief Description / Message <span className="text-[#C29F60]">*</span>
                          </label>
                          <textarea
                            name="message"
                            value={form.message}
                            onChange={handleChange}
                            rows={4}
                            maxLength={300}
                            placeholder="Briefly describe your condition..."
                            data-testid="booking-input-message"
                            className={`w-full px-4 py-3.5 bg-[#F9F9F6] rounded-xl text-sm text-[#1E3F33] placeholder:text-[#8FA998] focus:outline-none focus:ring-2 transition-all resize-none border ${errors.message
                                ? "border-red-500 focus:ring-red-200"
                                : "border-[#D8DDD9] focus:ring-[#1E3F33]/20 focus:border-[#1E3F33]"
                              }`}
                          />

                          {errors.message && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.message}
                            </p>
                          )}

                          <div className="flex justify-end mt-1">
                            <span className="text-xs text-[#8FA998]">
                              {form.message.length}/50
                            </span>
                          </div>
                        </div>

                        {/* Error */}
                        {error && (
                          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded-xl">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            {error}
                          </div>
                        )}

                        {/* Submit */}
                        <button
                          type="submit"
                          disabled={submitting}
                          data-testid="booking-submit-btn"
                          className="w-full bg-[#1E3F33] text-[#F9F9F6] py-4 rounded-full text-sm font-bold hover:bg-[#2C5545] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {submitting ? (
                            <>
                              <span className="w-4 h-4 border-2 border-[#F9F9F6]/30 border-t-[#F9F9F6] rounded-full animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Calendar className="w-4 h-4" />
                              Send Message
                            </>
                          )}
                        </button>

                      </form>
                    </>
                  )}
                </motion.div>
              </div>

              {/* RIGHT: SIDEBAR */}
              <div className="lg:col-span-5 space-y-6">
                {/* What Happens After Booking */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.25 }}
                  className="bg-[#F2EFE9] rounded-2xl p-7"
                  data-testid="booking-after-info"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <Clock className="w-5 h-5 text-[#1E3F33]" />
                    <h3 className="font-serif text-xl text-[#1E3F33]">What Happens After Booking?</h3>
                  </div>
                  <div className="space-y-4">
                    {afterBooking.map((item, i) => (
                      <div key={i} className="flex items-start gap-3" data-testid={`booking-after-step-${i}`}>
                        <div className="w-7 h-7 rounded-full bg-[#1E3F33] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-[#F9F9F6]">{i + 1}</span>
                        </div>
                        <p className="text-sm text-[#4A6359] leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Consultation Locations */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.35 }}
                  className="bg-[#F2EFE9] rounded-2xl p-7"
                  data-testid="booking-locations"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <MapPin className="w-5 h-5 text-[#1E3F33]" />
                    <h3 className="font-serif text-xl text-[#1E3F33]">Consultation Locations</h3>
                  </div>
                  <div className="space-y-3">
                    {hospitals.map((h, i) => (
                      <div key={i} className="flex items-center gap-3 bg-[#F9F9F6] rounded-xl p-4" data-testid={`booking-location-${i}`}>
                        <Building2 className="w-4 h-4 text-[#8FA998] flex-shrink-0" />
                        <span className="text-sm font-medium text-[#1E3F33]">{h}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Quick Contact */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.45 }}
                  className="bg-[#1E3F33] rounded-2xl p-7 text-center"
                  data-testid="booking-quick-contact"
                >
                  <h3 className="font-serif text-xl text-[#F9F9F6] mb-3">Need Urgent Assistance?</h3>
                  <p className="text-sm text-[#8FA998] mb-5">Contact us directly for immediate help</p>
                  <div className="flex flex-col gap-3">
                    <a
                      href={`tel:${webConfig.PHONE}`}
                      data-testid="booking-sidebar-call"
                      className="inline-flex items-center justify-center gap-2 bg-[#F9F9F6] text-[#1E3F33] px-6 py-3 rounded-full text-sm font-bold hover:bg-[#E6E7E2] transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Call Now
                    </a>
                    <a
                      href="https://wa.me/916291000829"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="booking-sidebar-whatsapp"
                      className="inline-flex items-center justify-center gap-2 border-2 border-[#F9F9F6] text-[#F9F9F6] px-6 py-3 rounded-full text-sm font-bold hover:bg-[#F9F9F6] hover:text-[#1E3F33] transition-all"
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp
                    </a>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── WHO SHOULD BOOK ─── */}
        <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="booking-who-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
              >
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                  Who Should Book
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="booking-who-heading">
                  Who Should Book an Appointment?
                </h2>
                <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                  Early consultation ensures better diagnosis and more treatment options.
                </p>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={stagger}
                className="space-y-0"
              >
                {whoShouldBook.map((item, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    data-testid={`booking-who-item-${i}`}
                    className="flex items-start gap-4 py-4 border-b border-[#D8DDD9] last:border-0"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#1E3F33]/10 flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-4 h-4 text-[#1E3F33]" />
                    </div>
                    <p className="text-base text-[#1E3F33] font-medium leading-relaxed">{item}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── CONSULTATION TYPES ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="booking-types-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-12"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Types of Consultation
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="booking-types-heading">
                Consultations Available
              </h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {consultationTypes.map((type, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`booking-type-card-${i}`}
                  className="bg-[#F2EFE9] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center mb-4">
                    <Sparkles className="w-5 h-5 text-[#1E3F33]" />
                  </div>
                  <p className="text-base font-semibold text-[#1E3F33]">{type}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── WHY TRUST ─── */}
        <section className="py-20 md:py-28 bg-[#1E3F33]" data-testid="booking-trust-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-12"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Why Patients Trust Us
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-2xl" data-testid="booking-trust-heading">
                Why Patients Trust Dr. Neha Agarwal
              </h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {trustPoints.map((point, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`booking-trust-item-${i}`}
                  className="flex items-start gap-3 bg-[#2C5545] rounded-xl p-5 border border-[#8FA998]/15"
                >
                  <Heart className="w-4 h-4 text-[#C29F60] mt-0.5 flex-shrink-0" />
                  <p className="text-sm sm:text-base text-[#F9F9F6] font-medium">{point}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── AREAS SERVED + FINAL CTA ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="booking-areas-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Areas Served
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl mx-auto mb-4" data-testid="booking-areas-heading">
                Patients Across Kolkata
              </h2>
              <p className="text-base text-[#4A6359] max-w-lg mx-auto leading-relaxed mb-8">
                Mukundapur, EM Bypass, Salt Lake, Garia, Kasba, Ballygunge, South Kolkata and surrounding areas.
              </p>
              <a
                href="#booking-form-section"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('[data-testid="booking-form-section"]')?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="booking-scroll-to-form"
                className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#2C5545] transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Book Your Consultation Today
              </a>
            </motion.div>
          </div>
        </section>

        {/* ─── FOOTER ─── */}
        {/* <footer className="bg-[#1E3F33] py-10" data-testid="booking-footer">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-xs text-[#F9F9F6]/30">
                &copy; {new Date().getFullYear()} Dr. Neha Agarwal. All rights reserved.
              </p>
              <Link to="/" className="text-xs text-[#F9F9F6]/30 hover:text-[#F9F9F6]/60 transition-colors flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to Home
              </Link>
            </div>
          </div>
        </footer> */}
      </div>
    </>
  );
}
